var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { Arc } from '../shapes/index';
import { colorWithAlpha, mixColors } from '../utils/color';
var DEFAULT_START = Math.PI * 0.75;
var DEFAULT_SWEEP = Math.PI * 1.5;
function textTopY(y, fontSize) {
    return y - fontSize * 0.5;
}
function withAlpha(color, alpha) {
    var _a;
    return (_a = colorWithAlpha(color, alpha)) !== null && _a !== void 0 ? _a : color;
}
/** Professional semicircular dial — layered bezel, ticks, value arc, needle hub */
export function buildDialGauge(app, group, style, opts) {
    var e_1, _a;
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0;
    var size = opts.size;
    var cx = size / 2;
    var inset = Math.max(4, Math.min(14, size * 0.1));
    var r = size / 2 - inset;
    var tickOutset = Math.max(3, size * 0.035);
    var majorLen = Math.max(5, size * 0.085);
    var minorLen = Math.max(3, size * 0.05);
    var startAngle = (_b = opts.startAngle) !== null && _b !== void 0 ? _b : DEFAULT_START;
    var sweep = (_c = opts.sweepAngle) !== null && _c !== void 0 ? _c : DEFAULT_SWEEP;
    var endAngle = startAngle + sweep;
    var trackW = (_d = style.trackWidth) !== null && _d !== void 0 ? _d : Math.max(6, size * 0.055);
    var tickColor = (_f = (_e = style.tickColor) !== null && _e !== void 0 ? _e : style.textMuted) !== null && _f !== void 0 ? _f : '#cbd5e1';
    var face = (_g = style.faceColor) !== null && _g !== void 0 ? _g : '#0a0a0a';
    var bezel = (_h = style.bezelColor) !== null && _h !== void 0 ? _h : style.trackColor;
    var accent = (_j = style.accentColor) !== null && _j !== void 0 ? _j : style.needleColor;
    var shell = (_k = mixColors(face, '#000000', 0.55)) !== null && _k !== void 0 ? _k : '#050505';
    var trackUnderlay = (_l = mixColors(face, '#000000', 0.35)) !== null && _l !== void 0 ? _l : '#1a1f2e';
    var hubFill = (_m = mixColors(face, '#ffffff', 0.12)) !== null && _m !== void 0 ? _m : '#1f2937';
    var format = (_o = opts.formatValue) !== null && _o !== void 0 ? _o : (function (v) { return String(Math.round(v)); });
    var formatTick = (_p = opts.formatTickLabel) !== null && _p !== void 0 ? _p : (function (v) { return String(Math.round(v)); });
    var tickCount = (_q = opts.tickCount) !== null && _q !== void 0 ? _q : 8;
    var hubOuter = Math.max(5, size * 0.065);
    var hubInner = Math.max(2.5, size * 0.032);
    var needleW = Math.max(2, size * 0.014);
    var shadow = size >= 120
        ? { color: 'rgba(0,0,0,0.45)', blur: Math.min(12, size / 12), offsetX: 0, offsetY: Math.min(4, size / 30) }
        : undefined;
    group.add(app.circle({
        x: cx - r - tickOutset - 2,
        y: cx - r - tickOutset - 2,
        radius: r + tickOutset + 2,
        fill: shell,
        stroke: bezel,
        strokeWidth: Math.max(1.5, size * 0.018),
        shadow: shadow,
        listening: false,
    }), app.circle({
        x: cx - r - tickOutset + 1,
        y: cx - r - tickOutset + 1,
        radius: r + tickOutset - 1,
        fill: face,
        stroke: withAlpha(bezel, 0.55),
        strokeWidth: 1,
        listening: false,
    }));
    group.add(new Arc({
        x: cx - r,
        y: cx - r,
        radius: r,
        startAngle: startAngle,
        endAngle: endAngle,
        fill: null,
        stroke: trackUnderlay,
        strokeWidth: trackW + 2,
        listening: false,
    }));
    if ((_r = opts.colorZones) === null || _r === void 0 ? void 0 : _r.length) {
        try {
            for (var _1 = __values(opts.colorZones), _2 = _1.next(); !_2.done; _2 = _1.next()) {
                var zone = _2.value;
                group.add(new Arc({
                    x: cx - r,
                    y: cx - r,
                    radius: r,
                    startAngle: startAngle + sweep * zone.from,
                    endAngle: startAngle + sweep * zone.to,
                    fill: null,
                    stroke: zone.color,
                    strokeWidth: trackW,
                    listening: false,
                }));
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_2 && !_2.done && (_a = _1.return)) _a.call(_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }
    else {
        var redlineStart = opts.redlineFrom;
        if (redlineStart !== undefined && redlineStart < 1) {
            group.add(new Arc({
                x: cx - r,
                y: cx - r,
                radius: r,
                startAngle: startAngle + sweep * redlineStart,
                endAngle: endAngle,
                fill: null,
                stroke: (_s = style.redlineColor) !== null && _s !== void 0 ? _s : '#ef4444',
                strokeWidth: trackW,
                listening: false,
            }));
        }
        group.add(new Arc({
            x: cx - r,
            y: cx - r,
            radius: r,
            startAngle: startAngle,
            endAngle: redlineStart !== undefined ? startAngle + sweep * redlineStart : endAngle,
            fill: null,
            stroke: style.trackColor,
            strokeWidth: trackW,
            listening: false,
        }));
    }
    var valueAngle = startAngle + (opts.value / Math.max(opts.max, 1)) * sweep;
    var valueArc = opts.value > 0 && size >= 96
        ? new Arc({
            x: cx - r,
            y: cx - r,
            radius: r,
            startAngle: startAngle,
            endAngle: valueAngle,
            fill: null,
            stroke: withAlpha(accent, 0.32),
            strokeWidth: Math.max(2, trackW * 0.38),
            listening: false,
        })
        : undefined;
    if (valueArc)
        group.add(valueArc);
    for (var i = 0; i <= tickCount; i++) {
        var t = i / tickCount;
        var a = startAngle + sweep * t;
        var cos = Math.cos(a);
        var sin = Math.sin(a);
        var major = i % 2 === 0;
        var outer = r - tickOutset * 0.35;
        var inner = outer - (major ? majorLen : minorLen);
        var dx = (outer - inner) * cos;
        var dy = (outer - inner) * sin;
        group.add(app.line({
            x: cx + inner * cos,
            y: cx + inner * sin,
            x2: dx,
            y2: dy,
            stroke: major ? tickColor : withAlpha(tickColor, 0.55),
            strokeWidth: major ? Math.max(1.5, size * 0.016) : 1,
            lineCap: 'round',
            listening: false,
        }));
        if (opts.showTickLabels && major && size >= 96) {
            var labelVal = formatTick(opts.max * t);
            var lr = r - Math.max(majorLen + 6, size * 0.16);
            var lx = cx + lr * cos;
            var ly = cx + lr * sin;
            var skipTop = sin < -0.35 && Math.abs(cos) < 0.55;
            if (skipTop)
                continue;
            var labelSize = Math.max(7, size * 0.085);
            group.add(app.text({
                text: labelVal,
                x: lx,
                y: textTopY(ly, labelSize),
                fontSize: labelSize,
                fontWeight: '500',
                fill: (_u = (_t = style.tickLabelColor) !== null && _t !== void 0 ? _t : style.textMuted) !== null && _u !== void 0 ? _u : style.textColor,
                textAlign: 'center',
                metadata: { textBoxWidth: Math.max(18, size * 0.22), textBoxCenterY: ly },
                listening: false,
            }));
        }
    }
    if (opts.title && size >= 72) {
        var titleSize = (_v = opts.titleFontSize) !== null && _v !== void 0 ? _v : Math.max(7, size * 0.068);
        var titleY = cx - r * 0.5;
        group.add(app.text({
            text: opts.title.toUpperCase(),
            x: cx,
            y: textTopY(titleY, titleSize),
            fontSize: titleSize,
            fontWeight: '600',
            fill: (_x = (_w = style.textMuted) !== null && _w !== void 0 ? _w : style.tickLabelColor) !== null && _x !== void 0 ? _x : style.textColor,
            textAlign: 'center',
            metadata: { textBoxWidth: size, textBoxCenterY: titleY },
            listening: false,
        }));
    }
    var angle = valueAngle;
    var needleLen = r * 0.76;
    var needle = app.line({
        x: cx,
        y: cx,
        x2: needleLen * Math.cos(angle),
        y2: needleLen * Math.sin(angle),
        stroke: style.needleColor,
        strokeWidth: needleW,
        lineCap: 'round',
        shadow: { color: 'rgba(0,0,0,0.4)', blur: 4, offsetX: 1, offsetY: 2 },
        listening: false,
    });
    group.add(needle);
    group.add(app.circle({
        x: cx - hubOuter,
        y: cx - hubOuter,
        radius: hubOuter,
        fill: hubFill,
        stroke: bezel,
        strokeWidth: 1,
        listening: false,
    }), app.circle({
        x: cx - hubInner,
        y: cx - hubInner,
        radius: hubInner,
        fill: style.needleColor,
        listening: false,
    }));
    var valueSize = (_y = opts.valueFontSize) !== null && _y !== void 0 ? _y : Math.max(11, size * 0.115);
    var valueY = cx + r * 0.08;
    var valueText = app.text(__assign(__assign({ text: format(opts.value), x: cx, y: textTopY(valueY, valueSize), fontSize: valueSize, fontWeight: 'bold', fill: style.textColor, textAlign: 'center', metadata: { textBoxWidth: size, textBoxCenterY: valueY } }, (opts.ariaLive ? { ariaLive: opts.ariaLive } : {})), { listening: false }));
    group.add(valueText);
    var unitText;
    if (opts.unit) {
        var unitSize = (_z = opts.unitFontSize) !== null && _z !== void 0 ? _z : Math.max(8, size * 0.072);
        var unitY = cx + r * 0.26;
        unitText = app.text({
            text: opts.unit.trim(),
            x: cx,
            y: textTopY(unitY, unitSize),
            fontSize: unitSize,
            fontWeight: '500',
            fill: (_0 = style.textMuted) !== null && _0 !== void 0 ? _0 : style.textColor,
            textAlign: 'center',
            metadata: { textBoxWidth: size, textBoxCenterY: unitY },
            listening: false,
        });
        group.add(unitText);
    }
    return { needle: needle, valueArc: valueArc, valueText: valueText, unitText: unitText };
}
export function dialNeedleAngle(value, max, start, sweep) {
    if (start === void 0) { start = DEFAULT_START; }
    if (sweep === void 0) { sweep = DEFAULT_SWEEP; }
    return start + (value / Math.max(max, 1)) * sweep;
}
export function updateDialNeedle(needle, _cx, value, max, r, start, sweep, counterweight, valueArc) {
    if (start === void 0) { start = DEFAULT_START; }
    if (sweep === void 0) { sweep = DEFAULT_SWEEP; }
    var angle = dialNeedleAngle(value, max, start, sweep);
    var len = r * 0.76;
    needle.x2 = len * Math.cos(angle);
    needle.y2 = len * Math.sin(angle);
    if (counterweight) {
        var counterLen = r * 0.11;
        counterweight.x2 = -counterLen * Math.cos(angle);
        counterweight.y2 = -counterLen * Math.sin(angle);
    }
    if (valueArc) {
        valueArc.endAngle = angle;
        valueArc.visible = value > 0;
    }
}
